<script setup lang="ts">
import { computed } from 'vue';
import ImprovementCategory from './ImprovementCategory.vue';
import { createTextStyle } from '@/common/customisation/utils/styleUtils';
import {
  useCsatUserSuggestionsHeadingCustomisation,
  useCsatSuggestionOptionsCustomisation,
} from '../composables/useCsatUserSuggestionsCustomisation';
import { useFeedbackSelections } from '../composables/useFeedbackSelections';
import type { ImprovementOption } from '../types/csat.types';
import { ChatRateMode } from '@/common/api/models.pleased';

interface Props {
  entityType?: ChatRateMode;
  selectedImprovements: string[];
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (event: 'improvement-toggled', payload: { id: string; label: string }): void;
}>();

const {
  textContent: improvementQuestionText,
  textColor: improvementQuestionTextColor,
  isVisible: isSuggestionsHeadingVisible,
} = useCsatUserSuggestionsHeadingCustomisation();

const { isVisible: isSuggestionOptionsVisible } = useCsatSuggestionOptionsCustomisation();

const { feedbackSelections } = useFeedbackSelections(props.entityType);

const improvementOptions = computed<ImprovementOption[]>(() => {
  return feedbackSelections.value.map((selection) => ({
    id: String(selection.feedbackSelectionId),
    label: selection.name,
  }));
});

const improvementQuestionStyle = computed(() => createTextStyle(improvementQuestionTextColor));

const handleImprovementToggle = (option: ImprovementOption) => {
  emit('improvement-toggled', { id: option.id, label: option.label });
};
</script>

<template>
  <div v-if="isSuggestionsHeadingVisible || isSuggestionOptionsVisible" class="csat-improvement-section">
    <div
      v-if="isSuggestionsHeadingVisible"
      class="csat-improvement-section__question theme-font-main"
      :style="improvementQuestionStyle"
    >
      {{ improvementQuestionText }}
    </div>

    <div v-if="isSuggestionOptionsVisible" class="csat-improvement-section__options">
      <ImprovementCategory
        v-for="option in improvementOptions"
        :key="option.id"
        :label="option.label"
        :is-selected="props.selectedImprovements.includes(option.id)"
        @click="handleImprovementToggle(option)"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '@/assets/scss/mixin.scss';

.csat-improvement-section {
  &__question {
    text-align: center;
    margin-bottom: 16px;
  }

  &__options {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 24px;
    width: 100%;
    box-sizing: border-box;

    @include respond(phone) {
      gap: 8px;
    }
  }
}
</style>
